import type { DotRender } from './decor'
import { PROFILE_SAMPLES } from './profiles'
import {
  hullOfCircles,
  polyPath,
  profileFromPolygon,
  unionOfCirclesProfile,
  type Point,
  type Silhouette
} from './shape'
import { CUSTOM_ICONS, type CustomIconDraft } from './customIcons'

/**
 * Faceless body icons at eyed-sparkle weight (peak ≈ 1.04 — larger than the orbit oval).
 *
 * Built-ins come from the measured exclamation blobs (`exclaim` / `alert`).
 * Add your own in `customIcons.ts`.
 */

/** Match customiser `etoile` peak so icons share the main body's visual weight. */
export const ICON_PEAK = 1.04

/** Orbit oval short half-axis in ball units — icons must clear this. */
export const ORBIT_RX_BALL = 26.6 / 50

export type BuiltinIconId = 'bang' | 'alert' | 'query' | 'amp' | 'plus'
export type IconId = string

export interface IconDef {
  id: IconId
  radii: number[]
  cy: number
  /** Profile rotation, radians (italic alert). */
  rot?: number
  /** Optional detached dot under the glyph (bang / query / alert tear). */
  dot?: { x: number; y: number; r: number; rot?: number; pathScale?: number }
  /** Optional non-circular path for the dot (alert teardrop). */
  dotPath?: string
}

export function normalizeRadii(radii: number[], max = ICON_PEAK): number[] {
  const peak = Math.max(...radii)
  if (peak <= 0) return radii
  const k = max / peak
  return radii.map((r) => r * k)
}

/**
 * Farthest point of the posed glyph from the origin — bar + offset + tilt + dot.
 * Normalising only the profile peak left `alert` huge: its cy was scaled by a
 * larger factor than bang, then added on top of a 1.04 bar.
 */
export function iconWorldExtent(def: Pick<IconDef, 'radii' | 'cy' | 'rot' | 'dot'>): number {
  const rot = def.rot ?? 0
  const cr = Math.cos(rot)
  const sr = Math.sin(rot)
  let max = 0
  const n = def.radii.length
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2
    const r = def.radii[i] ?? 0
    const lx = Math.cos(a) * r
    const ly = Math.sin(a) * r
    const x = lx * cr - ly * sr
    const y = lx * sr + ly * cr + def.cy
    const d = Math.hypot(x, y)
    if (d > max) max = d
  }
  if (def.dot) {
    const d = Math.hypot(def.dot.x, def.dot.y) + def.dot.r
    if (d > max) max = d
  }
  return max
}

/** Uniform scale so the posed glyph's world peak is `ICON_PEAK`. */
export function fitIcon(def: IconDef, peak = ICON_PEAK): IconDef {
  const ext = iconWorldExtent(def)
  if (ext <= 0) return def
  const k = peak / ext
  if (Math.abs(k - 1) < 1e-6) return def
  return {
    ...def,
    radii: def.radii.map((r) => r * k),
    cy: def.cy * k,
    dot: def.dot
      ? { ...def.dot, x: def.dot.x * k, y: def.dot.y * k, r: def.dot.r * k }
      : undefined
  }
}

/* ------------------------------------------------ measured "!" blobs (video units) */

/** Upright "!" bar centre (video). */
export const EXCLAIM_BAR_CY = -0.1875

/** Upright "!" bar — tapered frustum, top/bottom = 1.76 (measured). */
export const EXCLAIM_BAR_RADII = profileFromPolygon(
  hullOfCircles(0, -0.505, 0.132, 0, 0.13, 0.075),
  0,
  EXCLAIM_BAR_CY
)

/** Upright "!" disc dot (video). */
export const EXCLAIM_DOT = { x: -0.012, y: 0.526, r: 0.113 } as const

/** Italic "!" capsule bar (video, `alert`). */
export const ALERT_BAR_RADII = profileFromPolygon(
  hullOfCircles(0, -0.2535, 0.1345, 0, 0.2535, 0.1345),
  0,
  0
)

/** Italic "!" teardrop path (video). */
export const ALERT_TEAR_PATH = polyPath(hullOfCircles(0, 0, 0.118, 0, 0.172, 0.012))

/** Scale factor: video "!" peak → sparkle weight. */
const EXCLAIM_MEAS_PEAK = 0.505 + 0.132
export const EXCLAIM_TO_ICON = ICON_PEAK / EXCLAIM_MEAS_PEAK

/** Italic alert bar half-length (video) → sparkle weight. */
const ALERT_MEAS_PEAK = 0.2535 + 0.1345
export const ALERT_TO_ICON = ICON_PEAK / ALERT_MEAS_PEAK

/** Scale a video-space offset into icon space. */
export function scaleExclaim(n: number): number {
  return n * EXCLAIM_TO_ICON
}

export function scaleAlert(n: number): number {
  return n * ALERT_TO_ICON
}

/* ------------------------------------------------ builders (for customIcons.ts) */

/** Radial profile from a disc union — easiest way to sketch a blob. */
export function iconFromDiscs(
  discs: Array<{ x: number; y: number; r: number }>,
  peak = ICON_PEAK
): number[] {
  return normalizeRadii(unionOfCirclesProfile(discs), peak)
}

/** Radial profile from a closed polygon (ball units). */
export function iconFromPolygon(poly: Point[], cx = 0, cy = 0, peak = ICON_PEAK): number[] {
  return normalizeRadii(profileFromPolygon(poly, cx, cy), peak)
}

/** Two-circle capsule / frustum bar (same helper as the measured "!"). */
export function iconFromBar(
  x1: number,
  y1: number,
  r1: number,
  x2: number,
  y2: number,
  r2: number,
  cy = 0,
  peak = ICON_PEAK
): number[] {
  return normalizeRadii(barRadiiRaw(x1, y1, r1, x2, y2, r2, cy), peak)
}

/** Unnormalised bar profile — compose with `mergeProfiles` then normalise once. */
export function barRadiiRaw(
  x1: number,
  y1: number,
  r1: number,
  x2: number,
  y2: number,
  r2: number,
  cy = 0
): number[] {
  return profileFromPolygon(hullOfCircles(x1, y1, r1, x2, y2, r2), 0, cy)
}

/**
 * Outer silhouette of several bars (pointwise max). Use this for crisp glyphs —
 * stacked `discs` read as soft blobs; bars keep a stem / stroke.
 */
export function mergeProfiles(...profiles: number[][]): number[] {
  const out = new Array<number>(PROFILE_SAMPLES).fill(0)
  for (const p of profiles) {
    for (let i = 0; i < PROFILE_SAMPLES; i++) {
      const r = p[i] ?? 0
      if (r > out[i]!) out[i] = r
    }
  }
  return out
}

/** Crisp multi-bar glyph, normalised to sparkle weight. */
export function iconFromBars(
  bars: Array<{
    x1: number
    y1: number
    r1: number
    x2: number
    y2: number
    r2: number
    cy?: number
  }>,
  peak = ICON_PEAK
): number[] {
  return normalizeRadii(
    mergeProfiles(...bars.map((b) => barRadiiRaw(b.x1, b.y1, b.r1, b.x2, b.y2, b.r2, b.cy ?? 0))),
    peak
  )
}

/* ------------------------------------------------ compile drafts → IconDef */

export function compileIcon(draft: CustomIconDraft): IconDef | null {
  const id = draft.id.trim()
  if (!id) return null

  let radii: number[] | null = null
  let cy = draft.cy ?? 0

  if (draft.radii) {
    if (draft.radii.length !== PROFILE_SAMPLES) return null
    radii = normalizeRadii([...draft.radii])
  } else if (draft.discs?.length) {
    radii = iconFromDiscs(draft.discs)
  } else if (draft.polygon?.length) {
    radii = iconFromPolygon(draft.polygon, draft.polyCx ?? 0, draft.polyCy ?? 0)
    if (draft.cy === undefined && draft.polyCy !== undefined) cy = draft.polyCy
  } else if (draft.bars?.length) {
    radii = iconFromBars(draft.bars)
  } else if (draft.bar) {
    const b = draft.bar
    cy = draft.cy ?? b.cy ?? 0
    radii = iconFromBar(b.x1, b.y1, b.r1, b.x2, b.y2, b.r2, cy)
  }

  if (!radii) return null

  return fitIcon({
    id,
    radii,
    cy,
    dot: draft.dot,
    dotPath: draft.dotPath
  })
}

/* ------------------------------------------------ built-ins: "!" bars + crisp strokes */

/**
 * Amp / query used to be disc unions and read as soft blobs. Built-ins are now
 * bar strokes (same family as the measured "!"), merged with pointwise max.
 */
const BUILTIN_ICONS: IconDef[] = [
  {
    id: 'bang',
    radii: normalizeRadii(EXCLAIM_BAR_RADII),
    cy: scaleExclaim(EXCLAIM_BAR_CY),
    dot: {
      x: scaleExclaim(EXCLAIM_DOT.x),
      y: scaleExclaim(EXCLAIM_DOT.y),
      r: scaleExclaim(EXCLAIM_DOT.r)
    }
  },
  {
    // Italic "!" — measured 17.7° tilt; round disc (video uses a tear — too
    // fragile once scaled to sparkle weight, reads as a jagged stub).
    id: 'alert',
    radii: normalizeRadii(ALERT_BAR_RADII),
    cy: scaleAlert(-0.325),
    rot: (17.7 * Math.PI) / 180,
    dot: (() => {
      const tilt = (17.7 * Math.PI) / 180
      const along = scaleAlert(0.58)
      const cy = scaleAlert(-0.325)
      return {
        x: -Math.sin(tilt) * along,
        y: cy + Math.cos(tilt) * along,
        r: scaleAlert(0.118),
        rot: 17.7
      }
    })()
  },
  {
    // Candy-cane "?": upright stem + two short strokes for the hook, disc below.
    id: 'query',
    radii: iconFromBars([
      { x1: 0, y1: -0.12, r1: 0.11, x2: 0, y2: 0.38, r2: 0.085 },
      { x1: -0.02, y1: -0.52, r1: 0.12, x2: 0.38, y2: -0.42, r2: 0.11 },
      { x1: 0.38, y1: -0.42, r1: 0.11, x2: 0.28, y2: -0.08, r2: 0.1 }
    ]),
    cy: 0,
    dot: { x: 0, y: 0.72, r: 0.11 }
  },
  {
    // "&"-ish knot from three strokes (loops + diagonal), not a disc cloud.
    id: 'amp',
    radii: iconFromBars([
      { x1: -0.28, y1: -0.42, r1: 0.14, x2: 0.22, y2: -0.08, r2: 0.13 },
      { x1: 0.22, y1: -0.08, r1: 0.13, x2: -0.3, y2: 0.42, r2: 0.14 },
      { x1: -0.05, y1: 0.05, r1: 0.12, x2: 0.42, y2: 0.48, r2: 0.11 }
    ]),
    cy: 0
  },
  {
    // Straight plus — clearest multi-bar glyph.
    id: 'plus',
    radii: iconFromBars([
      { x1: 0, y1: -0.55, r1: 0.12, x2: 0, y2: 0.55, r2: 0.12 },
      { x1: -0.55, y1: 0, r1: 0.12, x2: 0.55, y2: 0, r2: 0.12 }
    ]),
    cy: 0
  }
].map((def) => fitIcon(def))

const CUSTOM_COMPILED = CUSTOM_ICONS.map(compileIcon).filter((d): d is IconDef => !!d)

/** All icons: built-ins first, then everything from `customIcons.ts`. */
export const ICONS: IconDef[] = [...BUILTIN_ICONS, ...CUSTOM_COMPILED]

/** Catalogue ids (built-ins + customs). */
export const ICON_IDS: IconId[] = ICONS.map((i) => i.id)

/**
 * Exclamation morph loop for the `icons` state — upright bang ⇄ italic alert only.
 * Amp / query / plus stay available via the `icon` prop on `glyph`.
 */
export const EXCLAIM_CYCLE: IconId[] = ['bang', 'alert']

/** Seconds per half-cycle (hold + morph into the next "!). */
export const EXCLAIM_CYCLE_SLOT = 1.6

export const ICON_BY_ID = new Map(ICONS.map((i) => [i.id, i]))

export function isIconId(id: string): boolean {
  return ICON_BY_ID.has(id)
}

export function iconSilhouette(id: IconId, pose: Partial<Silhouette> = {}): Silhouette {
  const def = ICON_BY_ID.get(id)
  if (!def) {
    return { radii: new Array(PROFILE_SAMPLES).fill(1), rot: 0, cx: 0, cy: 0, sx: 1, sy: 1, ...pose }
  }
  return {
    radii: [...def.radii],
    rot: def.rot ?? 0,
    cx: 0,
    cy: def.cy,
    sx: 1,
    sy: 1,
    ...pose
  }
}

export function iconDots(id: IconId, opacity = 1): DotRender[] {
  const def = ICON_BY_ID.get(id)
  if (!def?.dot) return []
  return [
    {
      x: def.dot.x,
      y: def.dot.y,
      r: def.dot.r,
      opacity,
      rot: def.dot.rot,
      pathScale: def.dot.pathScale,
      ...(def.dotPath ? { d: def.dotPath } : {})
    }
  ]
}

/** Resolve a prop value: catalogue id, or a raw `PROFILE_SAMPLES` radii array. */
export function resolveIcon(
  icon: IconId | string | number[] | null | undefined
): { radii: number[]; cy: number; rot?: number; dots: DotRender[] } | null {
  if (icon == null) return null
  if (Array.isArray(icon)) {
    if (icon.length !== PROFILE_SAMPLES) return null
    return { radii: normalizeRadii([...icon]), cy: 0, rot: 0, dots: [] }
  }
  if (typeof icon === 'string' && isIconId(icon)) {
    const def = ICON_BY_ID.get(icon)!
    return {
      radii: [...def.radii],
      cy: def.cy,
      rot: def.rot ?? 0,
      dots: iconDots(icon)
    }
  }
  return null
}

/** Soft morph between two catalogue icons (radii + cy + fading dots). */
export function blendIcons(
  a: IconId,
  b: IconId,
  k: number
): { sil: Silhouette; dots: DotRender[] } {
  const A = ICON_BY_ID.get(a) ?? ICON_BY_ID.get('bang')!
  const B = ICON_BY_ID.get(b) ?? ICON_BY_ID.get('bang')!
  const radii = A.radii.map((r, i) => r + ((B.radii[i] ?? r) - r) * k)
  const cy = A.cy + (B.cy - A.cy) * k
  let dRot = (B.rot ?? 0) - (A.rot ?? 0)
  while (dRot > Math.PI) dRot -= Math.PI * 2
  while (dRot < -Math.PI) dRot += Math.PI * 2
  const rot = (A.rot ?? 0) + dRot * k
  const dots: DotRender[] = []
  if (A.dot) {
    dots.push({
      x: A.dot.x,
      y: A.dot.y,
      r: A.dot.r,
      opacity: 1 - k,
      rot: A.dot.rot,
      pathScale: A.dot.pathScale,
      ...(A.dotPath ? { d: A.dotPath } : {})
    })
  }
  if (B.dot) {
    dots.push({
      x: B.dot.x,
      y: B.dot.y,
      r: B.dot.r,
      opacity: k,
      rot: B.dot.rot,
      pathScale: B.dot.pathScale,
      ...(B.dotPath ? { d: B.dotPath } : {})
    })
  }
  return {
    sil: { radii, rot, cx: 0, cy, sx: 1, sy: 1 },
    dots: dots.filter((d) => d.opacity > 0.02)
  }
}
